import React, { useEffect, useState } from 'react';
import { authToken } from '../../authToken';

import TaskListAddPanel from './TaskListAddPanel';
import TaskListEditPanel from './TaskListEditPanel';
import TaskListItemMenuPanel from './TaskListItemMenuPanel';
import { API_BASE_URL } from '../../config';

const API_TASKLIST_URL = `${API_BASE_URL}/tasks`;

// Explicit custom ordering for task lists.
// The rule is:
//  1) not done first
//  2) "available to do" first (validFrom <= today UTC)
//  3) then by availableTo/validTo (earlier first)
//  4) then by name
//  5) then by availableFrom/validFrom (earlier first)
//  6) then by id
// Keep this as a separate function so the ordering stays obvious and easy to tweak.
function sortTaskListsCustom(lists) {
  const toMillis = (v) => {
    if (v === null || v === undefined) return Number.POSITIVE_INFINITY;
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  };

  // Compare "availability" against today's date at 00:00:00 UTC.
  const getTodayUtcMidnightMillis = () => {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
  };
  const todayUtcMidnight = getTodayUtcMidnightMillis();
  const isAvailableToDo = (task) => toMillis(task.validFrom) <= todayUtcMidnight;

  const collator = new Intl.Collator('pl', { sensitivity: 'base' });

  return [...lists].sort((a, b) => {
    const doneA = !!a.done;
    const doneB = !!b.done;
    if (doneA !== doneB) return doneA ? 1 : -1;

    const availA = isAvailableToDo(a);
    const availB = isAvailableToDo(b);
    if (availA !== availB) return availA ? -1 : 1;

    const vt = toMillis(a.validTo) - toMillis(b.validTo);
    if (vt !== 0) return vt;

    const nameCmp = collator.compare(a.name ?? '', b.name ?? '');
    if (nameCmp !== 0) return nameCmp;

    const vf = toMillis(a.validFrom) - toMillis(b.validFrom);
    if (vf !== 0) return vf;

    return (a.id ?? 0) - (b.id ?? 0);
  });
}

function getTodayUtcMidnightMillis() {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
}

function isAvailableToDo(task, todayUtcMidnight) {
  const vf = new Date(task.validFrom).getTime();
  return Number.isFinite(vf) && vf <= todayUtcMidnight;
}

function isOverdue(task, todayUtcMidnight) {
  const vt = new Date(task.validTo).getTime();
  return Number.isFinite(vt) && vt < todayUtcMidnight;
}

function isDueToday(task, todayUtcMidnight) {
  const vt = new Date(task.validTo).getTime();
  return Number.isFinite(vt) && vt === todayUtcMidnight;
}

function hasInvalidDateRange(task) {
  const vf = new Date(task.validFrom).getTime();
  const vt = new Date(task.validTo).getTime();
  return Number.isFinite(vf) && Number.isFinite(vt) && vf > vt;
}

function getTaskColors(task, todayUtcMidnight) {
  // Priority order (most important first):
  // 1) invalid range (validFrom > validTo)
  // 2) done
  // 3) overdue
  // 4) due today
  // 5) available to do
  // 6) not available yet -> default
  if (hasInvalidDateRange(task)) {
    return { backgroundColor: '#FF0000', color: '#000000' };
  }

  if (task.done) {
    return { backgroundColor: '#f2f2f2', color: '#666666' };
  }

  if (isOverdue(task, todayUtcMidnight)) {
    return { backgroundColor: '#fff0f0', color: '#8a1f1f' };
  }

  if (isDueToday(task, todayUtcMidnight)) {
    return { backgroundColor: '#fff6e6', color: '#8a5a00' };
  }

  if (isAvailableToDo(task, todayUtcMidnight)) {
    return { backgroundColor: '#eefaea', color: '#175a2b' };
  }

  return null;
}

function TaskListPanel({ onClose }) {
  const [taskLists, setTaskLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addPanelInitialValues, setAddPanelInitialValues] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [menuIdx, setMenuIdx] = useState(null);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);

  const fetchLists = () => {
    setLoading(true);
    fetch(`${API_TASKLIST_URL}/actual`, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch task lists');
        return res.json();
      })
      .then(data => {
        setTaskLists(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };
  useEffect(() => { fetchLists(); }, []);

  const handleAdd = (task) => {
    fetch(API_TASKLIST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify(task)
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add task');
        return res.json();
      })
      .then(() => {
        setShowAddPanel(false);
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleEdit = (id) => {
    const t = taskLists.find(x => x.id === id);
    if (!t) return;
    setEditTask(t);
    setShowEditPanel(true);
    setMenuIdx(null);
  };

  const handleEditSave = (payload) => {
    fetch(`${API_TASKLIST_URL}/${payload.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify({
        name: payload.name,
        validFrom: payload.validFrom,
        validTo: payload.validTo,
        done: payload.done
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to update task');
        return res.json();
      })
      .then(() => {
        setShowEditPanel(false);
        setEditTask(null);
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleDelete = (id) => {
    setConfirmDeleteIdx(id);
    setMenuIdx(null);
  };

  const handleDone = (id) => {
    setMenuIdx(null);
    fetch(`${API_TASKLIST_URL}/${id}/done`, {
      method: 'PUT',
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to mark task as done');
        // API might return JSON or empty body; safest is to just refresh.
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleUndone = (id) => {
    setMenuIdx(null);
    fetch(`${API_TASKLIST_URL}/${id}/undone`, {
      method: 'PUT',
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to mark task as undone');
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleDoToday = (id) => {
    const t = taskLists.find(x => x.id === id);
    if (!t) return;
    setMenuIdx(null);

    const toMillis = (v) => {
      if (v === null || v === undefined) return NaN;
      if (typeof v === 'number') return v;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
      const d = new Date(v);
      const ms = d.getTime();
      return Number.isFinite(ms) ? ms : NaN;
    };

    const oldValidFrom = toMillis(t.validFrom);
    const oldValidTo = toMillis(t.validTo);
    const todayUtcMidnight = getTodayUtcMidnightMillis();

    if (!Number.isFinite(oldValidFrom) || !Number.isFinite(oldValidTo)) {
      setError('Nieprawidłowe daty zadania');
      return;
    }

    const delta = todayUtcMidnight - oldValidTo;
    const newValidFrom = oldValidFrom + delta;
    const newValidTo = todayUtcMidnight;

    fetch(`${API_TASKLIST_URL}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify({
        name: t.name,
        validFrom: newValidFrom,
        validTo: newValidTo,
        done: !!t.done,
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to update task');
        return res.json();
      })
      .then(() => {
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleDuplicate = (id) => {
    const t = taskLists.find(x => x.id === id);
    if (!t) return;
    setMenuIdx(null);
    setAddPanelInitialValues({
      name: t.name,
      validFrom: t.validFrom,
      validTo: t.validTo,
      done: false,
    });
    setShowAddPanel(true);
  };

  const confirmDelete = (id) => {
    fetch(`${API_TASKLIST_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete');
        setConfirmDeleteIdx(null);
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
        setConfirmDeleteIdx(null);
      });
  };

  const todayUtcMidnight = getTodayUtcMidnightMillis();

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '420px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '650px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>
      <h2 style={{ marginBottom: 16 }}>Zadania</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #6cf',
            background: '#eaf6ff',
            color: '#227',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onClick={() => {
            setAddPanelInitialValues(null);
            setShowAddPanel(true);
          }}
        >➕ Dodaj</button>
      </div>
      {loading && <div>Ładowanie...</div>}
      {error && <div style={{ color: 'red' }}>Błąd: {error}</div>}
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {sortTaskListsCustom(taskLists).map((list) => {
          const colorStyle = getTaskColors(list, todayUtcMidnight);
          return (
          <li
            key={list.id}
            style={{
              padding: '6px 0',
              borderBottom: '1px solid #eee',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              position: 'relative',
              userSelect: 'none',
              borderRadius: 10,
              paddingLeft: 10,
              paddingRight: 10,
              marginBottom: 3,
              ...(colorStyle ?? {}),
            }}
          >
            <span>
              <b>{list.name}</b> <span style={{color:'#aaa', fontSize:'0.95em'}}>({list.id})</span>
              <div style={{ marginTop: 0, lineHeight: 1.1 }}>
                <span style={{ color:'#888', fontSize:'0.82em' }}>Od: {new Date(list.validFrom).toISOString().slice(0,10)}</span>
                <span style={{ marginLeft:8, color:'#888', fontSize:'0.82em' }}>Do: {new Date(list.validTo).toISOString().slice(0,10)}</span>
              </div>
            </span>
            <button
              style={{
                background: 'none',
                border: 'none',
                fontSize: 22,
                cursor: 'pointer',
                padding: '0 8px',
                color: '#888',
                marginLeft: 8
              }}
              aria-label="Menu"
              onClick={e => {
                e.stopPropagation();
                setMenuIdx(list.id);
              }}
            >
              ⋯
            </button>
            {menuIdx === list.id && (
              <TaskListItemMenuPanel
                onEdit={() => handleEdit(list.id)}
                onDone={list.done ? null : () => handleDone(list.id)}
                onDoToday={list.done ? null : () => handleDoToday(list.id)}
                onUndone={list.done ? () => handleUndone(list.id) : null}
                onDuplicate={() => handleDuplicate(list.id)}
                onDelete={() => handleDelete(list.id)}
                onCancel={() => setMenuIdx(null)}
              />
            )}
            {confirmDeleteIdx === list.id && (
              <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                padding: 28,
                zIndex: 13000,
                minWidth: 240,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16
              }}>
                <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Usunąć zadanie <b>{list.name}</b>?</div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <button
                    onClick={() => confirmDelete(list.id)}
                    style={{
                      padding: '10px 24px',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 8,
                      border: '1px solid #f66',
                      background: '#fff0f0',
                      color: '#a22',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >Usuń</button>
                  <button
                    onClick={() => setConfirmDeleteIdx(null)}
                    style={{
                      padding: '10px 24px',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 8,
                      border: '1px solid #bbb',
                      background: '#f8f8f8',
                      color: '#555',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >Anuluj</button>
                </div>
              </div>
            )}
          </li>
          );
        })}
      </ul>
      {!loading && taskLists.length === 0 && <div>Brak zadań :0</div>}
      {showAddPanel && (
        <TaskListAddPanel
          onAdd={handleAdd}
          onCancel={() => {
            setShowAddPanel(false);
            setAddPanelInitialValues(null);
          }}
          initialValues={addPanelInitialValues}
        />
      )}

      {showEditPanel && editTask && (
        <TaskListEditPanel
          task={editTask}
          onSave={handleEditSave}
          onCancel={() => {
            setShowEditPanel(false);
            setEditTask(null);
          }}
        />
      )}
    </div>
  );
}

export default TaskListPanel;
